import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { lchService, type LchTenantCost } from '@/services/lch-service'

export function PricingHaulDumpPage() {
  const [lchCosts, setLchCosts] = useState<LchTenantCost[]>([])
  const [lchEdits, setLchEdits] = useState<Record<string, Partial<LchTenantCost>>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    lchService.listTenantCosts().then((data) => {
      setLchCosts(data)
      setLoaded(true)
    }).catch(() => setLoaded(true))
  }, [])

  const updateField = (workId: string, field: keyof LchTenantCost, value: number) => {
    setLchEdits(prev => ({
      ...prev,
      [workId]: { ...prev[workId], [field]: value },
    }))
  }

  const getValue = (cost: LchTenantCost, field: keyof LchTenantCost): number => {
    const edit = lchEdits[cost.workId]
    if (edit && edit[field] !== undefined) return edit[field] as number
    return cost[field] as number
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      for (const cost of lchCosts) {
        const edits = lchEdits[cost.workId]
        if (edits) {
          await lchService.upsertTenantCost(cost.workId, {
            dumpCost: edits.dumpCost ?? cost.dumpCost,
            truckingCostPerHr: edits.truckingCostPerHr ?? cost.truckingCostPerHr,
            maxTruckLoad: edits.maxTruckLoad ?? cost.maxTruckLoad,
            lchTime: edits.lchTime ?? cost.lchTime,
          })
        }
      }
      const updated = await lchService.listTenantCosts()
      setLchCosts(updated)
      setLchEdits({})
    } catch {
      // TODO: show error toast
    } finally {
      setIsSaving(false)
    }
  }

  if (!loaded) return null

  return (
    <div>
      {/* Page Header — matches pricing pages */}
      <div className="est-list-head">
        <h1 className="est-list-title">Haul & Dump Rates</h1>
      </div>

      <p className="lch-page-desc">
        Configure dump fees, trucking rates, and truck capacity for each removal material. These rates are used by the Haul & Dump Calculator in the estimate builder.
      </p>

      {/* Content card */}
      <div className="card est-list-card">
        <div className="lch-page-inner">
          {lchCosts.length === 0 ? (
            <p className="lch-settings-empty">No haul & dump material types configured yet.</p>
          ) : (
            <div className="lch-page-grid">
              {lchCosts.map(cost => (
                <div key={cost.workId} className="lch-page-card">
                  <div className="lch-card-header">{cost.name}</div>
                  <div className="lch-card-fields">
                    <div className="lch-field">
                      <Label>Dump Fee ($)</Label>
                      <Input
                        type="number" min="0" step="5"
                        value={getValue(cost, 'dumpCost') || ''}
                        onChange={(e) => updateField(cost.workId, 'dumpCost', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="lch-field">
                      <Label>Trucking $/hr</Label>
                      <Input
                        type="number" min="0" step="5"
                        value={getValue(cost, 'truckingCostPerHr') || ''}
                        onChange={(e) => updateField(cost.workId, 'truckingCostPerHr', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="lch-field">
                      <Label>Max Load (tons)</Label>
                      <Input
                        type="number" min="1" step="1"
                        value={getValue(cost, 'maxTruckLoad') || ''}
                        onChange={(e) => updateField(cost.workId, 'maxTruckLoad', parseFloat(e.target.value) || 1)}
                      />
                    </div>
                    {/* Round-Trip (hrs) was removed by request: new haul rows price
                        trucking by trucks × days × hours, not per round trip. The
                        stored value stays put (handleSave still sends it back
                        untouched) because rows created before crew pricing are still
                        calculated from it. */}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
        <Button
          onClick={handleSave}
          disabled={isSaving || Object.keys(lchEdits).length === 0}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  )
}
